import { type HeadersFunction, json, type LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import React from 'react';

import CmsPage from '~/sections/cms-page/CmsPage';
import { apiFetch } from '~/utils/apiFetch';
import { publicPageHeaders } from '~/utils/cacheControl.server';
import { buildSeoMeta, getRootOrigin, getShopName } from '~/utils/meta';

export async function loader({ params, request }: LoaderFunctionArgs) {
  const API_URL = process.env.PUBLIC_API_URL;
  const slug = params['*'];

  // Admin "preview" links carry an HMAC-signed token that lets the shop endpoint
  // serve drafts (disabled or outside their publish window). An invalid/expired
  // token silently behaves like the plain endpoint, so drafts still 404.
  const previewToken = new URL(request.url).searchParams.get('preview_token');
  const endpoint = previewToken
    ? `${API_URL}/api/v2/shop/pages/${slug}?preview_token=${encodeURIComponent(previewToken)}`
    : `${API_URL}/api/v2/shop/pages/${slug}`;

  const res = await apiFetch(endpoint, {}, { request });

  if (!res.ok) {
    throw new Response('Not Found', { status: 404 });
  }

  const data = await res.json();
  const isPreview = Boolean(previewToken);

  return json(
    { data, isPreview },
    // Never let a draft land in the CDN or browser cache.
    isPreview ? { headers: { 'Cache-Control': 'no-store' } } : undefined
  );
}

// Remix only puts loader headers on the document response when the route exports
// `headers` — without this the preview `Cache-Control: no-store` would be dropped.
export const headers: HeadersFunction = ({ loaderHeaders, parentHeaders }) => {
  const cacheControl = loaderHeaders.get('Cache-Control');
  if (!cacheControl) return publicPageHeaders(parentHeaders);

  const documentHeaders = publicPageHeaders(parentHeaders);
  documentHeaders.set('Cache-Control', cacheControl);
  return documentHeaders;
};

export const meta: MetaFunction<typeof loader> = ({ data, matches, location }) => {
  const page = data?.data;

  // `ogImage` is resolved by the API like for products and categories: the page's own
  // meta image, else the shop-wide default from Settings → SEO, already an absolute URL.
  const tags = buildSeoMeta({
    seo: page,
    fallbackTitle: page?.title || 'CMS Page',
    origin: getRootOrigin(matches),
    shopName: getShopName(matches),
    path: location.pathname,
  });

  if (data?.isPreview) {
    tags.push({ name: 'robots', content: 'noindex,nofollow' });
  } else if (page?.noIndex === true) {
    // "Hide from search engines" in admin: the backend already leaves the page out of the
    // sitemap, but it stays reachable by direct URL — this tag keeps it out of the index.
    // `follow` for the same reason as on an inactive product, see `ProductPage.tsx`.
    tags.push({ name: 'robots', content: 'noindex, follow' });
  }

  return tags;
};

const CmsPageRoute: React.FC = () => {
  const { data, isPreview } = useLoaderData<typeof loader>();
  return <CmsPage pageData={data} isPreview={isPreview} />;
};

export default CmsPageRoute;
